import "../sellscreendialogs.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useState,
} from "react";
import { ToastTypeEnum, TouchNumbers, useToastCreate } from "@onshopfront/components";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../../_Bootstrap/hooks";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { addPaymentToSale, type SaleAddPaymentOptions } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import Button from "../../../../Common/Buttons/Button";
import NoTranslate from "../../../../Common/NoTranslate";
import { getEnterpriseAuthToken } from "../../../../Data/_Bases/BaseEnterpriseRepository";
import { currency } from "../../../../Utilities/Generic/Numbers";
import { formatDate } from "../../../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getPolicy } from "../../../../Utilities/Platform/Policies/Policies";
import { getMainHost, getVendorSubdomain } from "../../../../Utilities/Platform/Request";

export const RequestPayGiftCard: FC<{
    amount: number;
    method: string;
    options: SaleAddPaymentOptions;
}> = ({
    amount: saleAmount,
    method,
    options,
}) => {
    const saleTotal = useShopfrontSelector(state => state.sellscreen.sale.totals.sale);
    const saleRemaining = useShopfrontSelector(state => state.sellscreen.sale.totals.remaining);

    const [ value, setValue ] = useState<number | null>(saleAmount);
    const [ paying, setPaying ] = useState(false);
    const dispatch = useShopfrontDispatch();
    const createToast = useToastCreate();

    const addCard = useCallback(async () => {
        if(!options.giftcard || options.giftcard === "auto") {
            return;
        }

        if(value === null) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "The specified amount is invalid",
            });

            return;
        }

        if(value === 0) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "You must specify an amount",
            });

            return;
        }

        if(Math.sign(value) !== Math.sign(saleAmount)) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: (
                    "Invalid amount specified, both the gift card payment and the sale total must be on the same " +
                    "side of zero"
                ),
            });

            return;
        }

        const currentAmount = options.giftcard.current_amount;

        if(Math.abs(value) > Math.abs(currentAmount)) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Invalid amount specified, the gift card does not have enough funds on it",
            });

            return;
        }

        if(Math.abs(value) > Math.abs(saleAmount)) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "You cannot overpay with a gift card",
            });

            return;
        }

        if(options.source !== "internal") {
            let source;
            const sources = getPolicy("gift_card_sources", []);

            for(let i = 0, l = sources.length; i < l; i++) {
                if(sources[i].connection === options.source) {
                    source = sources[i].address;
                    break;
                }
            }

            if(!source) {
                createToast({
                    type   : ToastTypeEnum.Error,
                    message: "Gift card source is no longer available, please try again",
                });

                return;
            }

            setPaying(true);

            const response = await fetch(`${source}/${options.giftcard.id}`, {
                method : "PUT",
                headers: {
                    "x-shopfront-token" : await getEnterpriseAuthToken(options.source),
                    "x-shopfront-vendor": getVendorSubdomain(),
                    "x-shopfront-host"  : getMainHost(),
                    "accept"            : "application/json",
                },
                body: JSON.stringify({
                    amount: value,
                }),
            });

            const body = await response.json();

            if(response.status !== 200) {
                console.error("Unable to redeem a gift card", body);
                let message;

                if(body.error && body.message) {
                    message = body.message;
                } else {
                    message = "Unknown Error";
                }

                createToast({
                    type   : ToastTypeEnum.Error,
                    message: `Unable to redeem gift card, response from Enterprise: ${message}`,
                });

                setPaying(false);

                return;
            }
        }

        dispatch(hideDialog());
        dispatch(addPaymentToSale(method, value, {
            ...options,
            exact: false,
        }));
    }, [ createToast, dispatch, method, options, saleAmount, value ]);

    useEffect(() => {
        if(!options.giftcard || options.giftcard === "auto") {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "The gift card has not been added to the sale correctly",
            });
            dispatch(hideDialog());

            return;
        }

        setValue(Math.min(saleAmount, options.giftcard.current_amount));
    }, [ saleAmount, options.giftcard, dispatch, createToast ]);

    if(!options.giftcard || options.giftcard === "auto") {
        return null;
    }

    const currentAmount = options.giftcard.current_amount;
    const giftCardAmount = value ?? 0;

    if(paying) {
        return (
            <div>
                <p>Processing payment...</p>
                <div>
                    <FontAwesomeLight icon="spinner-third" spin />
                </div>
            </div>
        );
    }

    return (
        <div className="request-pay-gift-card">
            <div className="gift-card">
                <div className="gift-card-details">
                    <table>
                        <tbody>
                            <tr>
                                <th>Code</th>
                                <td>{options.giftcard.code}</td>
                            </tr>
                            <tr>
                                <th>Expiry</th>
                                <td>{formatDate(options.giftcard.expiry)}</td>
                            </tr>
                            <tr>
                                <td />
                                <td />
                            </tr>
                            <tr>
                                <th>Gift Card Balance</th>
                                <td><NoTranslate>{currency(currentAmount)}</NoTranslate></td>
                            </tr>
                            <tr>
                                <th>After Payment Balance</th>
                                <td>
                                    <NoTranslate>{currency(currentAmount - giftCardAmount)}</NoTranslate>
                                </td>
                            </tr>
                            <tr>
                                <td />
                                <td />
                            </tr>
                            <tr>
                                <td />
                                <td />
                            </tr>
                            <tr>
                                <th>Sale Total</th>
                                <td><NoTranslate>{currency(saleTotal)}</NoTranslate></td>
                            </tr>
                            <tr>
                                <th>Current Sale Balance</th>
                                <td><NoTranslate>{currency(saleRemaining)}</NoTranslate></td>
                            </tr>
                            <tr>
                                <th>Sale Balance After Payment</th>
                                <td><NoTranslate>{currency(saleRemaining - giftCardAmount)}</NoTranslate>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="gift-card-payment">
                    <label>
                        Amount to pay
                        <TouchNumbers
                            mode="numeric"
                            input="currency"
                            min={saleAmount < 0 ? undefined : 0}
                            max={saleAmount < 0 ? 0 : undefined}
                            negative={saleAmount < 0}
                            value={value}
                            onChange={setValue}
                            onKeyDown={e => {
                                if(e.key === "Enter") {
                                    void addCard();

                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                            autoFocus
                        />
                    </label>
                </div>
            </div>
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={addCard}
                >
                    Pay
                </Button>
            </div>
        </div>
    );
};
